import * as React from "react"

import { X } from "lucide-react"

import { cn } from "@/lib/utils"

interface InputProps extends React.ComponentProps<"input"> {
  clearable?: boolean
  onClear?: () => void
}
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, clearable = false, onClear, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(props.value || "")

    const isControlled = props.value !== undefined
    const inputValue = isControlled ? props.value : internalValue

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(e.target.value)
      }
      if (props.onChange) {
        props.onChange(e)
      }
    }

    const handleClear = () => {
      if (onClear) {
        onClear()
      } else if (!isControlled) {
        setInternalValue("")
      }

      if (ref && typeof ref === "object" && ref.current) {
        ref.current.focus()
      }
    }

    const showClearButton = clearable && inputValue && String(inputValue).length > 0

    if (clearable) {
      return (
        <div className="relative">
          <input
            type={type}
            className={cn(
              "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base hover:shadow-sm transition-colors file:border-0 file:bg-transparent file:text-font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
              showClearButton && "pr-8",
              className
            )}
            ref={ref}
            value={inputValue}
            onChange={handleChange}
            {...props}
          />
          {
            showClearButton && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors hover:cursor-pointer"
                tabIndex={-1}
              >
                <X size={16} />
              </button>
            )
          }
        </div>
      )
    }

    return (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }